import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LayoutDashboard, LogOut, Shield } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const NAV_LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/members", label: "Members" },
  { to: "/businesses", label: "Business Directory" },
  { to: "/opportunities", label: "Opportunities" },
  { to: "/events", label: "Events" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const authed = user && typeof user === "object";

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur-md">
      <nav className="container-nx flex h-16 items-center justify-between">
        <Link to="/" onClick={() => setOpen(false)} data-testid="nav-logo-link">
          <Logo />
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-[#0B192C] bg-[#F5F3EF]" : "text-slate-600 hover:text-[#0B192C]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden items-center gap-2 lg:flex">
          {authed ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" className="btn-outline-navy px-4 py-2 text-xs" data-testid="nav-admin-btn">
                  <Shield className="h-4 w-4" /> Admin
                </Link>
              )}
              <Link to="/dashboard" className="btn-navy px-4 py-2 text-xs" data-testid="nav-dashboard-btn">
                <LayoutDashboard className="h-4 w-4" /> Dashboard
              </Link>
              <button onClick={handleLogout} className="btn-outline-navy px-4 py-2 text-xs" data-testid="nav-logout-btn">
                <LogOut className="h-4 w-4" /> Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-outline-navy px-5 py-2 text-xs" data-testid="nav-login-btn">
                Login
              </Link>
              <Link to="/register" className="btn-gold px-5 py-2 text-xs" data-testid="nav-join-btn">
                Join Now
              </Link>
            </>
          )}
        </div>

        <button
          className="flex h-10 w-10 items-center justify-center rounded-lg text-[#0B192C] lg:hidden"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {open && (
        <div className="border-t border-slate-200 bg-white lg:hidden" data-testid="mobile-menu">
          <div className="container-nx flex flex-col gap-1 py-4">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `rounded-lg px-4 py-3 text-sm font-medium ${
                    isActive ? "bg-[#F5F3EF] text-[#0B192C]" : "text-slate-600"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="mt-3 flex flex-col gap-2 border-t border-slate-100 pt-3">
              {authed ? (
                <>
                  {user.role === "admin" && (
                    <Link to="/admin" onClick={() => setOpen(false)} className="btn-outline-navy" data-testid="mobile-admin-btn">
                      <Shield className="h-4 w-4" /> Admin Panel
                    </Link>
                  )}
                  <Link to="/dashboard" onClick={() => setOpen(false)} className="btn-navy" data-testid="mobile-dashboard-btn">
                    <LayoutDashboard className="h-4 w-4" /> Dashboard
                  </Link>
                  <button onClick={handleLogout} className="btn-outline-navy" data-testid="mobile-logout-btn">
                    <LogOut className="h-4 w-4" /> Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="btn-outline-navy" data-testid="mobile-login-btn">
                    Login
                  </Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="btn-gold" data-testid="mobile-join-btn">
                    Join Now
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export default Navbar;
